import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

export const APPT_ACTIONS_NEW_TIME_LABEL = 'New date & time';
export const APPT_ACTIONS_TIME_REQUIRED = 'Choose a new date and time.';
export const APPT_ACTIONS_TIME_PAST = 'Choose a time in the future.';
export const APPT_ACTIONS_TIME_UNCHANGED = 'Choose a different time.';
export const APPT_ACTIONS_SAVE_LABEL = 'Save new time';
export const APPT_ACTIONS_SAVING_LABEL = 'Saving…';
export const APPT_ACTIONS_CANCEL_LABEL = 'Back';

/**
 * Formats an instant as `YYYY-MM-DDTHH:mm` in LOCAL time — the value shape of
 * a native `datetime-local` input (toLocalDatetimeValue precedent from
 * AppointmentEnrollmentView.tsx:36–43, taking epoch ms for `min={...}`).
 */
export function toLocalInputValue(epochMs: number): string {
  const d = new Date(epochMs);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Props = {
  /** Currently scheduled time (ISO UTC) — prefills the input and anchors the
   *  "unchanged" check. Undefined disables the unchanged comparison. */
  currentTimeUtc?: string;
  /** Disables inputs/buttons while the parent's mutation is in flight. */
  isSubmitting?: boolean;
  /** Server-side failure message — rendered while the user's input is preserved. */
  serverError?: string | null;
  /** Emits the new appointment time as a UTC ISO 8601 string. */
  onSave: (newAppointmentTimeUtc: string) => void;
  onCancel: () => void;
};

/**
 * Inline reschedule form — native `datetime-local` input with hand-rolled
 * touched-state validation (plan Decision 3 — no react-hook-form/zod in this
 * repo). Guards run before `onSave` is ever invoked: empty/invalid, past
 * times, and the unchanged current time are all rejected client-side (the
 * server 422 remains the backstop). The native `min` attribute blocks past
 * selection in supporting browsers.
 */
export default function RescheduleForm({
  currentTimeUtc,
  isSubmitting = false,
  serverError = null,
  onSave,
  onCancel,
}: Props) {
  const currentMs = currentTimeUtc ? new Date(currentTimeUtc).getTime() : NaN;
  const currentLocalValue = isNaN(currentMs) ? '' : toLocalInputValue(currentMs);

  const [timeInput, setTimeInput] = useState<string>(currentLocalValue);
  const [touched, setTouched] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Move focus into the form when it opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validate = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (trimmed === '') return APPT_ACTIONS_TIME_REQUIRED;
    const nextMs = new Date(trimmed).getTime();
    if (isNaN(nextMs)) return APPT_ACTIONS_TIME_REQUIRED;
    if (nextMs < Date.now()) return APPT_ACTIONS_TIME_PAST;
    if (currentLocalValue !== '' && trimmed === currentLocalValue) {
      return APPT_ACTIONS_TIME_UNCHANGED;
    }
    return null;
  };

  const validationError = validate(timeInput);
  const showValidationError = touched && validationError !== null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setTouched(true);
    // Pre-submit guard: empty/invalid/past/unchanged are rejected here, before
    // any payload is emitted (the server 422 is the backstop, not the guard).
    if (validate(timeInput) !== null) return;
    onSave(new Date(timeInput.trim()).toISOString());
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-3">
      <div>
        <label htmlFor="appointment-reschedule-time" className="block text-xs text-gray-600 mb-1">
          {APPT_ACTIONS_NEW_TIME_LABEL}
        </label>
        <input
          ref={inputRef}
          id="appointment-reschedule-time"
          type="datetime-local"
          value={timeInput}
          min={toLocalInputValue(Date.now())}
          onChange={(e) => setTimeInput(e.target.value)}
          onBlur={() => setTouched(true)}
          disabled={isSubmitting}
          aria-invalid={showValidationError}
          aria-describedby={showValidationError ? 'appointment-reschedule-time-error' : undefined}
          className={`shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:cursor-not-allowed disabled:opacity-50 ${
            showValidationError ? 'border-red-500' : ''
          }`}
        />
        {showValidationError && (
          <p id="appointment-reschedule-time-error" className="mt-1 text-xs text-red-600">
            {validationError}
          </p>
        )}
      </div>

      {serverError && (
        <p role="alert" className="text-sm text-red-600">
          {serverError}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? APPT_ACTIONS_SAVING_LABEL : APPT_ACTIONS_SAVE_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-lg bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {APPT_ACTIONS_CANCEL_LABEL}
        </button>
      </div>
    </form>
  );
}
